"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.baseEngine = baseEngine;
exports.assertLaunchable = assertLaunchable;
exports.enginesFor = enginesFor;
const models_1 = require("../models");
const engine_1 = require("../engine");
const flight_1 = require("../engine/flight");
/** Retuning to many settings (a drag sweep, say) must not grow the cache without bound. */
const MAX_CACHED = 64;
const tuned = new Map();
const base = new Map();
/** The engine on the model's untuned profile: conversions, pose and camera. */
function baseEngine(name, sport) {
    const key = `${name}/${sport}`;
    let engine = base.get(key);
    if (engine === undefined) {
        engine = (0, engine_1.createEngine)(baseProfile(name, sport));
        base.set(key, engine);
    }
    return engine;
}
/**
 * The model's profile on v1's venue. v1 accepts a model and sport with no
 * speed table (it draws courts for them, and computes NaN if asked to throw),
 * where the engine refuses: for those, a profile with the model's geometry,
 * the sport's ball and venue, and a launch mechanism that throws when used.
 * Pose, camera, coordinates and strokes work as in v1; launching and speed
 * conversions fail, naming the pair.
 */
function baseProfile(name, sport) {
    const venue = (0, engine_1.v1Venue)(sport);
    const result = (0, engine_1.loadProfile)(name, sport, { venue });
    if (result.ok) {
        return result.profile;
    }
    // the trainer's geometry is the model's, whatever the sport
    const sibling = models_1.Sports.map((other) => (0, engine_1.loadProfile)(name, other, { venue: (0, engine_1.v1Venue)(other) })).find((r) => r.ok);
    if (sibling === undefined || !sibling.ok) {
        throw new Error(result.message);
    }
    return {
        trainer: { model: name, geometry: sibling.profile.trainer.geometry, mechanism: unavailableMechanism(result.message) },
        ball: engine_1.BALLS[engine_1.DEFAULT_BALL[sport]],
        venue,
        environment: sibling.profile.environment,
    };
}
/** Why each stand-in mechanism cannot launch. */
const unavailable = new WeakMap();
/** Throws, naming the pair, if the profile has no speed table to launch with. */
function assertLaunchable({ trainer: { mechanism } }) {
    const message = unavailable.get(mechanism);
    if (message !== undefined) {
        throw new Error(`${message}, which launching and speed conversions need`);
    }
}
/** A launch mechanism for a pair with no speed table: every use throws. */
function unavailableMechanism(message) {
    const fail = () => {
        throw new Error(`${message}, which launching and speed conversions need`);
    };
    const mechanism = {
        toMotors: fail,
        fromMotors: fail,
        deflectionCorrection: fail,
        limits: { maxSpeed: NaN, minSpeed: NaN, maxSpin: NaN, rpm: { min: NaN, max: NaN, maxSpin: NaN } },
        speedForRpm: fail,
        rpmForSpeed: fail,
        maxSpinAt: fail,
        spinLevels: { toRpm: fail, fromRpm: fail, maxAt: fail },
    };
    unavailable.set(mechanism, message);
    return mechanism;
}
/** The engines for the model as it is tuned right now. */
function enginesFor(model) {
    const { simParams: s, constants: c, ballParams: b } = model;
    const key = [
        model.name,
        model.sport,
        s.timeStep,
        s.wallBounceEnabled,
        s.netBounceEnabled,
        c.gravity,
        c.airDensity,
        c.courtRestCoeff,
        c.wallRestCoeff,
        c.netRestCoeff,
        b.diameter,
        b.mass,
        b.drag,
    ].join("/");
    let engines = tuned.get(key);
    if (engines === undefined) {
        if (tuned.size >= MAX_CACHED) {
            tuned.clear();
        }
        const profile = tunedProfile(model);
        let solver;
        engines = {
            profile,
            kernel: (0, flight_1.createFlight)(profile, { integrator: "legacy", timeStep: s.timeStep }),
            get solver() {
                solver ??= (0, engine_1.createEngine)(profile, { spinModel: "v1", peakHeight: "v1" });
                return solver;
            },
        };
        tuned.set(key, engines);
    }
    return engines;
}
/**
 * The model's profile with its tunables applied: v1's venue less the walls
 * or net a caller turned off, its restitutions, gravity, air and ball.
 */
function tunedProfile({ name, sport, simParams, constants, ballParams }) {
    const profile = baseProfile(name, sport);
    const { venue } = profile;
    const panels = venue.arena.panels
        .filter((panel) => (panel.kind === "wall" ? simParams.wallBounceEnabled : simParams.netBounceEnabled))
        .map((panel) => ({
        ...panel,
        response: panel.kind === "wall"
            ? { kind: "bounce", restitution: constants.wallRestCoeff, tangential: 1 }
            : { kind: "bounce", restitution: constants.netRestCoeff, tangential: constants.netRestCoeff },
    }));
    return {
        trainer: profile.trainer,
        ball: { ...profile.ball, diameter: ballParams.diameter, mass: ballParams.mass, dragCoefficient: ballParams.drag },
        venue: { ...venue, arena: { ...venue.arena, ground: { restitution: constants.courtRestCoeff }, panels } },
        environment: { gravity: constants.gravity, airDensity: constants.airDensity },
    };
}
